/**
 * App-wide Mercure Subscriptions Configuration
 *
 * Each subscription defines:
 * - id:          Unique identifier for logging/debugging
 * - topic:       Topic pattern with :param placeholders (resolved at runtime)
 * - description: Human-readable description (dev tooling / logging)
 * - handler:     Async function receiving a deps object
 *
 * Handler deps: { client, userId, data, queryClient, toast, logout }
 *
 * Architecture note:
 * This file owns ALL real-time side-effects. Never put Mercure subscriptions
 * inside React hooks or components — AppMercureSubscriber wires everything here.
 */

import {
  getNotificationToastConfig,
  notificationToastDedupeKey,
} from "@components/ui/toast/toastBusiness";
import { homeDashboardQueryKeys } from "@features/home-dashboard";
import { congesDashboardQueryKeys } from "@features/home-dashboard/conges";
import { invalidateUserPermissions } from "@lib/api/contextInvalidation";
import { queryKeys } from "@lib/api/queryKeys";
import type { ToastFn } from "@lib/hooks/useToast";
import { normalizePermissionContext } from "@lib/permissions/parser";
import type { PermissionsSummary } from "@lib/permissions/summary.types";
import type { NotificationRecord } from "@lib/queries/notificationQueries";
import { fetchPermissionsForUserById } from "@lib/queries/permissionQueries";
import { navigateToAppPath } from "@lib/router";
import { usePermissionStore } from "@lib/stores/permissions.store";
import { useUISettings } from "@lib/stores/uiSettings";
import type { QueryClient } from "@tanstack/react-query";

// ─── Shared types ──────────────────────────────────────────────────────────────

export interface Subscription {
  id: string;
  topic: string;
  description: string;
  handler: (deps: HandlerDeps) => Promise<void>;
}

interface HandlerDeps {
  client: unknown;
  userId: string | number;
  data: Record<string, unknown>;
  queryClient: QueryClient;
  toast: ToastFn;
  logout: () => void;
}

// ─── Notification settings helper ─────────────────────────────────────────────

interface NotificationUISettings {
  sound: boolean;
  duration: number;
  showToast: boolean;
  desktop: boolean;
}

const DEFAULT_NOTIFICATION_SETTINGS: NotificationUISettings = {
  sound: true,
  duration: 3000,
  showToast: true,
  desktop: false,
};

/**
 * Read notification UI preferences from the canonical persisted Zustand store.
 * Safe to call outside React — reads the store state directly (the persist
 * middleware owns persistence; no direct storage access here).
 */
const getNotificationSettings = (): NotificationUISettings => {
  const { sound, duration, desktop } = useUISettings.getState().settings.notifications;
  return { ...DEFAULT_NOTIFICATION_SETTINGS, sound, duration, desktop };
};

// ─── Sound helper ──────────────────────────────────────────────────────────────

type SoundType = "info" | "success" | "warning" | "error";

const NOTIFICATION_SOUND_MAP: Record<string, SoundType> = {
  error: "error",
  critical: "error",
  warning: "warning",
  success: "success",
};

const playSound = async (type: string): Promise<void> => {
  const { playNotificationSound, isAudioSupported } = await import("@lib/utils/sound");
  if (!isAudioSupported()) return;
  const soundType: SoundType = NOTIFICATION_SOUND_MAP[type] ?? "info";
  playNotificationSound(soundType);
};

// ─── Toast helper ─────────────────────────────────────────────────────────────

/**
 * Show the rich business toast for a notification (type axis + optional topic
 * refinement via the toastBusiness registry — single resolution point, no
 * scattered switches). Repeated events morph ×N via their dedupe key, and the
 * "Voir" action navigates SPA-style through the router (never a full reload).
 */
const showNotificationToast = (
  notification: NotificationRecord,
  toast: HandlerDeps["toast"],
  duration: number,
): void => {
  if (!notification.title) return;

  const config = getNotificationToastConfig(notification);
  const actionUrl = notification.actionUrl;
  const options = {
    message: notification.message ?? undefined,
    duration,
    dedupeKey: notificationToastDedupeKey(notification),
    icon: config.icon,
    ...(actionUrl
      ? {
          actions: [
            {
              label: config.actionLabel ?? "Voir",
              onClick: () => navigateToAppPath(actionUrl),
            },
          ],
        }
      : {}),
  };

  toast.show(config.toastType, notification.title, options);
};

// ─── Cache helpers ─────────────────────────────────────────────────────────────

/**
 * Prepend a new notification to every cached list that would include it.
 * Skips lists filtered by isRead=true (new notifications are always unread).
 * Deduplicates by id to be idempotent.
 */
const prependNotificationToLists = (
  notification: NotificationRecord,
  queryClient: QueryClient,
): void => {
  queryClient
    .getQueriesData<NotificationRecord[]>({ queryKey: queryKeys.notifications.lists() })
    .forEach(([queryKey, cachedData]) => {
      if (!Array.isArray(cachedData)) return;

      // Don't prepend to lists explicitly filtered to isRead=true
      const keyString = JSON.stringify(queryKey);
      if (keyString.includes('"isRead":true')) return;

      // Deduplicate
      if (cachedData.some((n) => n.id === notification.id)) return;

      queryClient.setQueryData<NotificationRecord[]>(queryKey, [notification, ...cachedData]);
    });
};

/**
 * Optimistically update a single notification across all cached lists.
 * Used when a notification is mutated in another session via Mercure.
 */
const updateNotificationInLists = (
  updated: Partial<NotificationRecord> & { id: number },
  queryClient: QueryClient,
): void => {
  queryClient
    .getQueriesData<NotificationRecord[]>({ queryKey: queryKeys.notifications.lists() })
    .forEach(([queryKey, cachedData]) => {
      if (!Array.isArray(cachedData)) return;
      queryClient.setQueryData<NotificationRecord[]>(
        queryKey,
        cachedData.map((n) => (n.id === updated.id ? { ...n, ...updated } : n)),
      );
    });
};

/**
 * Remove a notification from every cached list.
 * Used on soft-delete Mercure events.
 */
const removeNotificationFromLists = (id: number, queryClient: QueryClient): void => {
  queryClient
    .getQueriesData<NotificationRecord[]>({ queryKey: queryKeys.notifications.lists() })
    .forEach(([queryKey, cachedData]) => {
      if (!Array.isArray(cachedData)) return;
      queryClient.setQueryData<NotificationRecord[]>(
        queryKey,
        cachedData.filter((n) => n.id !== id),
      );
    });
};

// ─── Collection topics (canonical registry) ───────────────────────────────────
// Mirror of the backend `MercureTopicBuilder::collectionTopic()` — key = API
// fetch resource (no `/api` prefix, as passed to `useTable` `resource`), value
// = canonical Mercure collection topic (WITH `/api` prefix). THE single source
// of truth for collection-level realtime: `useTableMercure` resolves its topic
// here when no explicit `mercureTopic` is provided, so the two vocabularies
// (HTTP fetch path vs Mercure topic) can't silently drift again.

export const COLLECTION_TOPICS: Record<string, string> = {
  "/tickets": "/api/tickets",
  "/declaration_ecotaxes": "/api/ecotaxes",
  "/ventes/promotions": "/api/promotions",
  "/ventes/grilles-tarifaires": "/api/grilles_tarifaires",
  "/declaration_fabrications": "/api/declarations",
  "/cloture_o_fs": "/api/clotures_of",
  "/mouvement_stocks": "/api/mouvements",
  "/univers_commercials": "/api/univers_commerciaux",
  "/famille_articles": "/api/famille_articles",
};

// ─── Subscriptions ─────────────────────────────────────────────────────────────

export const APP_SUBSCRIPTIONS = [
  // ── Permissions ───────────────────────────────────────────────────────────────
  {
    id: "permissions",
    topic: "/api/users/:userId/permissions",
    description:
      "Permission updates (granted, revoked, updated) — fetches fresh data and syncs store",
    handler: async ({ userId, data, queryClient, toast, logout }: HandlerDeps) => {
      /**
       * Fetch fresh permissions from the API and update the Zustand store.
       * This is the authoritative path — TanStack Query invalidation is a
       * secondary signal for components that read from the query cache.
       */
      const syncPermissionsStore = async () => {
        try {
          const freshData = await fetchPermissionsForUserById(userId);
          const normalizedContext = normalizePermissionContext(freshData.context ?? {});

          usePermissionStore.getState().setPermissions({
            permissions: (freshData.permissionsArray ?? []) as readonly string[],
            hasWildcard:
              freshData.hasWildcard ?? freshData.permissionsArray?.includes("*") ?? false,
            context: normalizedContext,
            summary: (freshData.summary as PermissionsSummary) ?? null,
          });
        } catch (error) {
          console.error("[Mercure] Failed to sync permissions store:", error);
        }
      };

      /**
       * Sync the Zustand store, then re-sync the TanStack Query cache
       * (`permissions.byUser(userId)` + the whole `permissions` branch).
       * The store is not the only source of truth — AuthProvider reads the
       * query cache — so both must move together, or the two diverge until a
       * reload. `refetchType: "active"` refetches the mounted query now
       * instead of leaving it stale for the rest of the session.
       * Runs even when the store sync failed: marking the cache stale is what
       * lets the mounted query retry and surface a visible error instead of
       * silently serving empty permissions.
       */
      const syncPermissionsAndCache = async () => {
        await syncPermissionsStore();
        invalidateUserPermissions(queryClient, userId);
      };

      switch (data?.type) {
        case "permissions.updated":
          toast.info("Permissions mises à jour.");
          await syncPermissionsAndCache();
          break;

        case "permissions.granted":
          toast.success("De nouveaux accès vous ont été accordés.");
          await syncPermissionsAndCache();
          break;

        case "permissions.revoked":
          toast.error("Certains de vos accès ont été révoqués.");
          await syncPermissionsAndCache();
          break;

        case "user.disabled":
          toast.error("Votre compte a été désactivé. Déconnexion...");
          // Awaited, unlike the interactive logout call sites: nothing follows in
          // this branch, and logout() never rejects, so the handler can afford to
          // let the revocation complete before the EventSource is torn down.
          await logout();
          break;

        default:
          break;
      }
    },
  },

  // ── Notifications ─────────────────────────────────────────────────────────────
  {
    id: "notifications",
    topic: "/api/notifications/:userId",
    description: "Real-time notification events (created, updated, deleted)",
    handler: async ({ data, queryClient, toast }: HandlerDeps) => {
      if (!data?.id) return;

      const notification = data as unknown as NotificationRecord;
      const settings = getNotificationSettings();

      // API Platform Mercure publishes the full serialized entity on mutation.
      // We disambiguate events by an explicit `eventType` field. If the backend
      // doesn't publish this field yet, we fall back to "notification.created"
      // so existing behaviour is preserved.
      const eventType =
        typeof data.eventType === "string" ? data.eventType : "notification.created";

      switch (eventType) {
        // ── New notification ────────────────────────────────────────────────────
        case "notification.created": {
          // 1. Optimistically prepend to relevant cached lists
          prependNotificationToLists(notification, queryClient);

          // 2. Invalidate the badge count — let the server be authoritative
          queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.unreadCount(),
          });

          // 2b. Bump the header badge (React nonce in ContentHeader re-triggers
          // the animation) and blip the Mercure dot on any live activity
          window.dispatchEvent(new CustomEvent("notif-count-bump"));
          window.dispatchEvent(new CustomEvent("mercure:event"));

          // 3. Sound — follows the resolved semantic toastType (from the
          // toastBusiness registry) so an error/critical rings as such.
          if (settings.sound) {
            await playSound(getNotificationToastConfig(notification).toastType);
          }

          // 4. Toast
          if (settings.showToast !== false) {
            showNotificationToast(notification, toast, settings.duration);
          }

          // 5. Desktop notification — fires only when the user enabled the
          // toggle AND granted the browser permission (honest flow, 8.6)
          if (
            settings.desktop &&
            typeof window !== "undefined" &&
            "Notification" in window &&
            Notification.permission === "granted"
          ) {
            new Notification(notification.title ?? "Nouvelle notification", {
              body: notification.message ?? undefined,
              tag: String(notification.id),
            });
          }

          break;
        }

        // ── Read/unread toggled in another session ──────────────────────────────
        case "notification.updated": {
          updateNotificationInLists(
            {
              id: notification.id,
              isRead: notification.isRead,
              readAt: notification.readAt,
            },
            queryClient,
          );

          // Re-sync badge — we can't safely delta it without knowing the
          // previous isRead state across all cached lists
          queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.unreadCount(),
          });

          break;
        }

        // ── Soft-deleted ────────────────────────────────────────────────────────
        case "notification.deleted": {
          removeNotificationFromLists(notification.id, queryClient);

          queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.unreadCount(),
          });

          break;
        }

        // ── Unknown event type ──────────────────────────────────────────────────
        default: {
          // Safe fallback: full invalidation rather than silently dropping
          queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.all,
          });
          break;
        }
      }
    },
  },

  // ── Home Dashboards ─────────────────────────────────────────────────────────
  {
    id: "dashboard-conges",
    topic: "/api/dashboard/conges",
    description: "Conges dashboard invalidation (summary, balances, compteurs)",
    handler: async ({ queryClient }: HandlerDeps) => {
      queryClient.invalidateQueries({
        queryKey: congesDashboardQueryKeys.summary(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.rh.compteursSolde.all(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.rh.correctionsSolde.all(),
      });
    },
  },
  {
    id: "dashboard-summary",
    topic: "/api/dashboard/summary",
    description: "Home dashboard summary invalidation",
    handler: async ({ queryClient }: HandlerDeps) => {
      queryClient.invalidateQueries({
        queryKey: homeDashboardQueryKeys.summary(),
      });
    },
  },
];

export default APP_SUBSCRIPTIONS;

// ─── Workflow Subscriptions ───────────────────────────────────────────────────
// Dynamic subscriptions based on user permissions (modules + accessible filiales).
// Topics follow the pattern: /api/workflow/{resource}/{filiale_code}

/**
 * Module → Mercure resource mapping for workflow subscriptions.
 * Keys are permission module names; values are backend workflow resource identifiers.
 */
export const MODULE_WORKFLOW_RESOURCES: Record<string, string[]> = {
  rh: ["demande_absences", "feuille_de_temps", "contrats", "avenants"],
  ventes: ["commande_clients", "facture_clients", "bon_expeditions"],
  achats: ["bon_receptions", "factures_fournisseurs"],
  stock: ["inventaires"],
  production: ["ordres_fabrications"],
};

/**
 * Handler for workflow Mercure events.
 * Invalidates query caches for the affected resource and logs transitions in dev.
 */
const workflowHandler = async ({ data, queryClient }: HandlerDeps) => {
  if (!data) return;

  // Backend publishes { event: 'workflow.transition', resource, ... }.
  // Accept both `event` and legacy `type` keys.
  const payload = data as Record<string, unknown>;
  const eventType = (payload.event as string | undefined) ?? (payload.type as string | undefined);
  const resource = payload.resource as string | undefined;

  if (!eventType || !resource) return;

  // Generic invalidation: invalidate all query keys containing the resource name.
  // This avoids a hardcoded module→queryKey mapping and stays DRY with TanStack Query.
  queryClient.invalidateQueries({
    predicate: (query) => {
      const keyString = JSON.stringify(query.queryKey);
      return keyString.includes(resource);
    },
  });

  if (import.meta.env?.DEV) {
    console.log("[Mercure] Workflow transition:", { eventType, resource, data });
  }
};

/**
 * Generate workflow Mercure subscriptions from user permissions.
 *
 * For each module the user has access to, subscribes to workflow topics
 * for every resource in that module × every accessible filiale.
 *
 * @param filialeCode       - User's primary filiale code (unused currently, reserved)
 * @param accessibleFiliales - Filiales the user can access
 * @param modules           - Module names the user has permissions for
 * @returns Array of Mercure subscription configs
 */
export function getWorkflowSubscriptions(
  filialeCode: string,
  accessibleFiliales: readonly string[],
  modules: readonly string[],
): Subscription[] {
  const subscriptions: Subscription[] = [];

  for (const mod of modules) {
    const resources = MODULE_WORKFLOW_RESOURCES[mod] ?? [];
    for (const resource of resources) {
      for (const filiale of accessibleFiliales) {
        const id = `workflow.${resource}.${filiale}`;
        subscriptions.push({
          id,
          topic: `/api/workflow/${resource}/${filiale}`,
          description: `Workflow transitions: ${resource} (${filiale})`,
          handler: workflowHandler,
        });
      }
    }
  }

  return subscriptions;
}
